import React, { useEffect, useLayoutEffect, useState } from 'react';
import Pagination from '@mui/material/Pagination';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Modal from 'react-bootstrap/Modal';
import Spinner from 'react-bootstrap/Spinner';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

import { useTax1099Api } from '../../resources/hooks/api/useTax1099ApiHook';
import './VendorPaymentsModal.css';
import '../../assets/css/ios-switch.css';

export interface PaymentItem {
  id: number;
  payment_date: string;
  gross_amount: number;
  payment_method: number;
  is_1099_reportable: boolean;
  reportable_form_type?: string | null;
  reportable_box?: string | null;
  reference_number?: string | null;
  memo?: string | null;
  source: number;
  net_amount?: number | null;
}

interface Props {
  show: boolean;
  vendorId: number;
  vendorName: string;
  vendorCode: string;
  branchId: number;
  /** The year the vendor row's expense count and threshold were scoped to. */
  taxYear: number;
  onClose: () => void;
}

const PAGE_SIZE = 25;

const VendorPaymentsModal: React.FC<Props> = ({
  show,
  vendorId,
  vendorName,
  vendorCode,
  branchId,
  taxYear,
  onClose,
}) => {
  const { t } = useTranslation('Tax1099Vendors');
  const { fetchVendorPayments } = useTax1099Api();

  const [loading, setLoading] = useState(false);
  const [payments, setPayments] = useState<PaymentItem[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [reportableTotal, setReportableTotal] = useState(0);
  // Opens on the filing year, because that is what the vendor row counted and
  // what the $600/$2,000 threshold is judged against. Showing every year here
  // under a "YTD" heading made one vendor read as 9 payments and $17,185
  // against a row saying 3 payments and below threshold -- both were right
  // about different periods, which is worse than either being wrong.
  const [allYears, setAllYears] = useState(false);
  const [page, setPage] = useState(1);
  const [resultsElement, setResultsElement] = useState<HTMLDivElement | null>(null);
  const [resultsHeight, setResultsHeight] = useState(260);

  useLayoutEffect(() => {
    const results = resultsElement;
    if (!show || !results) return undefined;
    const measure = () => setResultsHeight(Math.max(260, Math.min(420, results.getBoundingClientRect().height)));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(results);
    return () => observer.disconnect();
  }, [show, resultsElement]);

  useEffect(() => {
    setPage(1);
    if (!show) {
      setAllYears(false);
    }
  }, [show, vendorId, branchId, taxYear]);

  useLayoutEffect(() => {
    const table = resultsElement?.querySelector('.table-responsive');
    if (table) table.scrollTop = 0;
  }, [payments, resultsElement]);

  useEffect(() => {
    if (!show || !vendorId || !branchId) {
      return;
    }
    setLoading(true);
    let active = true;
    const params: Record<string, any> = {
      vendor_id: vendorId, branch_id: branchId, page, page_size: PAGE_SIZE,
    };
    if (!allYears) {
      params.tax_year = taxYear;
    }
    fetchVendorPayments(
      params,
      (message: string, response: any) => {
        if (!active) return;
        const data = response?.data?.data ?? {};
        setPayments(data.results ?? []);
        setTotalCount(data.count ?? 0);
        setReportableTotal(data.reportable_total ?? 0);
        setLoading(false);
      },
      (message: string) => {
        if (!active) return;
        setLoading(false);
        toast.error(message || t('errors.load_payments_failed', 'Could not load vendor expenses'));
      }
    );
    return () => { active = false; };
  }, [show, vendorId, branchId, taxYear, allYears, page]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <Modal show={show} onHide={onClose} size="lg" centered>
      <Modal.Header closeButton>
        <div>
          <Modal.Title className="h5 mb-0">
            {t('payments.modal_title', 'Vendor Expenses & Payments')}
          </Modal.Title>
          <small className="text-muted">
            {vendorName} ({vendorCode})
          </small>
        </div>
      </Modal.Header>
      <Modal.Body>
        {/* Summary Metric Strip */}
        <div className="d-flex gap-3 mb-3 p-3 bg-light rounded">
          <div>
            <span className="text-muted small d-block">
              {allYears
                ? t('payments.total_transactions_all', 'Transactions (all years)')
                : t('payments.total_transactions_year', 'Transactions in {{year}}', { year: taxYear })}
            </span>
            <strong className="h6 mb-0">{totalCount}</strong>
          </div>
          <div className="border-start ps-3">
            <span className="text-muted small d-block">
              {allYears
                ? t('payments.reportable_total_all', '1099 Reportable (all years)')
                : t('payments.reportable_total_year', '{{year}} 1099 Reportable', { year: taxYear })}
            </span>
            <strong className="h6 mb-0 text-success">
              {formatCurrency(reportableTotal)}
            </strong>
          </div>
          <div className="border-start ps-3">
            <span className="text-muted small d-block">
              {t('payments.period', 'Period')}
            </span>
            <div className="ios-switch-field">
              <input
                type="checkbox"
                className="ios-switch"
                id="vendor-payments-all-years"
                checked={allYears}
                onChange={(event) => {
                  setAllYears(event.target.checked);
                  setPage(1);
                }}
              />
              <label
                className="small fw-semibold"
                htmlFor="vendor-payments-all-years"
              >
                {t('payments.show_all_years', 'Show all years')}
              </label>
            </div>
          </div>
          <div className="border-start ps-3 ms-auto text-end">
            <span className="text-muted small d-block">{t('payments.compliance_label', 'IRC § 6050W Compliance')}</span>
            <span className="badge bg-secondary text-white" style={{ fontSize: '11px' }}>
              {t('payments.card_rails_excluded', 'Card Rails Excluded')}
            </span>
          </div>
        </div>

        {/* Keep the rows during refetches and animate to the new content height. */}
        <div
          className="position-relative vendor-payments-results"
          data-testid="vendor-payments-results"
          style={{ height: resultsHeight, overflow: 'hidden' }}
          aria-busy={loading}
        >
        {loading && payments.length > 0 && (
          <div
            className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
            style={{ zIndex: 2, background: 'rgba(255,255,255,0.6)' }}
          >
            <Spinner animation="border" variant="primary" size="sm" />
          </div>
        )}
        <div ref={setResultsElement} data-testid="vendor-payments-results-content">
        {loading && payments.length === 0 ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" size="sm" />
            <p className="text-muted mt-2 small">{t('messages.loading', 'Loading...')}</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="text-center py-5 text-muted">
            <p className="mb-0">
              {t('payments.no_records', 'No expense or payment records found for this vendor.')}
            </p>
            <small>
              {t('payments.no_records_hint', 'Run "Auto-Sync RevRex Expenses" or "Sync from QuickBooks" in the Vendor Center to ingest payments.')}
            </small>
          </div>
        ) : (
          <div
            className="table-responsive"
            style={{
              maxHeight: '420px',
              opacity: loading ? 0.5 : 1,
              transition: 'opacity 120ms ease-in-out',
            }}
          >
            <table className="table table-sm table-hover align-middle mb-0">
              <thead className="table-light sticky-top">
                <tr>
                  <th>{t('payments.date', 'Date')}</th>
                  <th>{t('payments.reference', 'Ref / Memo')}</th>
                  <th>{t('payments.method_header', 'Payment Method')}</th>
                  <th>{t('payments.source_header', 'Source')}</th>
                  <th>{t('payments.reportable', '1099')}</th>
                  <th className="text-end">{t('payments.gross', 'Gross Amount')}</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((item) => (
                  <tr key={item.id}>
                    <td>
                      <small className="fw-semibold">{item.payment_date}</small>
                    </td>
                    <td>
                      <div>
                        {item.reference_number && (
                          <code className="me-1">{item.reference_number}</code>
                        )}
                        <small className="text-muted">{item.memo || '--'}</small>
                      </div>
                    </td>
                    <td>
                      <Badge bg="info" text="dark">
                        {t(`payments.method.${item.payment_method}`, t('payments.method.other', 'Other'))}
                      </Badge>
                    </td>
                    <td>
                      <Badge bg="light" text="dark" className="border">
                        {t(`payments.source.${item.source}`, t('payments.source.unknown', 'Unknown'))}
                      </Badge>
                    </td>
                    <td>
                      {item.is_1099_reportable ? (
                        <Badge bg="success">{t('payments.badge_reportable', 'Reportable')}</Badge>
                      ) : (
                        <Badge bg="secondary">{t('payments.badge_non_reportable', 'Non-Reportable')}</Badge>
                      )}
                      {item.reportable_box && (
                        <small className="d-block text-muted" style={{ fontSize: '10px' }}>
                          {item.reportable_form_type} {item.reportable_box.replace('_', '-')}
                        </small>
                      )}
                    </td>
                    <td className="text-end fw-bold">
                      {formatCurrency(item.gross_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        {totalCount > PAGE_SIZE && (
          <Pagination
            className="me-auto"
            count={Math.ceil(totalCount / PAGE_SIZE)}
            page={page}
            onChange={(_event, value) => setPage(value)}
            disabled={loading}
            size="small"
          />
        )}
        <Button variant="secondary" onClick={onClose}>
          {t('actions.close', 'Close')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default VendorPaymentsModal;
